/**
 * Push Notification registration helper.
 * 
 * Uses @capacitor/push-notifications to register for FCM push notifications
 * on iOS and Android. Sends the device token to the backend so it can be
 * associated with the user's community + unit.
 */

import { API_BASE } from './config'

// Track registration state to prevent duplicate registrations and enable diagnostics
let _pushRegistrationState = { status: 'idle', error: null, token: null, apiResponse: null }

// ── In-memory notification store ──────────────────────────────────────────
const STORAGE_KEY = 'smart_comm_notifications'
let _notifications = []
let _listeners = []

function _loadNotifications() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    _notifications = raw ? JSON.parse(raw) : []
  } catch { _notifications = [] }
}

function _saveNotifications() {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(_notifications)) } catch {}
  _listeners.forEach(fn => fn(_notifications))
}

/** Subscribe to notification changes. Returns unsubscribe function. */
export function subscribeNotifications(fn) {
  _listeners.push(fn)
  return () => { _listeners = _listeners.filter(l => l !== fn) }
}

/** Get current notifications list. */
export function getNotifications() {
  if (_notifications.length === 0) _loadNotifications()
  return [..._notifications]
}

/** Add a notification to the store (deduplicates within 30s window). */
export function addNotification({ title, body, data }) {
  _loadNotifications()
  // Deduplicate: skip if same title+body was added in the last 30 seconds
  const now = Date.now()
  const isDupe = _notifications.some(n => {
    const age = now - new Date(n.timestamp).getTime()
    return age < 30000 && n.title === (title || 'Notification') && n.body === (body || '')
  })
  if (isDupe) return

  _notifications.unshift({
    id: now.toString() + Math.random().toString(36).slice(2, 6),
    title: title || 'Notification',
    body: body || '',
    data: data || {},
    timestamp: new Date().toISOString(),
    read: false,
  })
  // Keep last 50
  if (_notifications.length > 50) _notifications = _notifications.slice(0, 50)
  _saveNotifications()
}

/** Mark a notification as read. */
export function markNotificationRead(id) {
  _loadNotifications()
  const n = _notifications.find(x => x.id === id)
  if (n) { n.read = true; _saveNotifications() }
}

/** Mark all notifications as read. */
export function markAllNotificationsRead() {
  _loadNotifications()
  _notifications.forEach(n => { n.read = true })
  _saveNotifications()
}

/** Clear all notifications. */
export function clearAllNotifications() {
  _notifications = []
  _saveNotifications()
}

/** Get unread count. */
export function getUnreadCount() {
  if (_notifications.length === 0) _loadNotifications()
  return _notifications.filter(n => !n.read).length
}

// Event-based notification tap handling
let _notifTapListeners = []
// Pending tap: if tap fires before any component mounts, replay it on first subscribe
let _pendingTapData = null

/** Subscribe to notification tap events. Returns unsubscribe function.
 *  fn receives the notification object (with .data.type etc) or null.
 */
export function onNotificationTap(fn) {
  // If a tap happened before any listener was registered, replay it now
  if (_pendingTapData !== null) {
    const snapshot = _pendingTapData
    _pendingTapData = null
    setTimeout(() => fn(snapshot), 0)
  }
  _notifTapListeners.push(fn)
  return () => { _notifTapListeners = _notifTapListeners.filter(l => l !== fn) }
}

function _fireNotificationTap(notifData) {
  if (_notifTapListeners.length === 0) {
    // No listeners yet — store for replay when a component mounts
    _pendingTapData = notifData || {}
  } else {
    _notifTapListeners.forEach(fn => fn(notifData || {}))
  }
}

/**
 * Get the current push registration state (for diagnostics).
 */
export function getPushRegistrationState() {
  return { ..._pushRegistrationState }
}

/**
 * Register for push notifications.
 * Should be called after the user has logged in and their user record
 * (with COMMUNITY_ID and UNIT_NBR) is available.
 * 
 * @param {object} params
 * @param {string} params.communityId - The user's community ID
 * @param {string} params.unitNbr - The user's unit number
 * @param {string} params.email - The user's email
 */
export async function registerPushNotifications({ communityId, unitNbr, email }) {
  _pushRegistrationState = { status: 'starting', error: null, token: null, apiResponse: null }

  // Validate required params
  if (!communityId || !unitNbr) {
    const msg = `missing required params: communityId=${communityId}, unitNbr=${unitNbr}`
    console.error('Push:', msg)
    _pushRegistrationState = { status: 'error', error: msg, token: null, apiResponse: null }
    return
  }

  console.log('Push: registerPushNotifications called', { communityId, unitNbr, email: email ? email.substring(0, 5) + '…' : '(none)' })

  // Only run on native platforms
  const isNative = typeof window !== 'undefined' && window.Capacitor &&
    typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform()

  const detectedPlatform = window.Capacitor?.getPlatform?.() || 'unknown'
  console.log('Push: platform=' + detectedPlatform + ', isNative=' + isNative)

  if (!isNative) {
    console.log('Push: not a native platform, skipping registration')
    _pushRegistrationState = { status: 'skipped-web', error: null, token: null, apiResponse: null }
    return
  }

  try {
    console.log('Push: importing @capacitor/push-notifications…')
    const { PushNotifications } = await import('@capacitor/push-notifications')
    console.log('Push: plugin imported successfully')

    // Check current permission status
    const permStatus = await PushNotifications.checkPermissions()
    console.log('Push: current permission status:', permStatus.receive)

    if (permStatus.receive === 'denied') {
      console.warn('Push: permissions denied by user, cannot register')
      _pushRegistrationState = { status: 'permission-denied', error: 'User denied notification permission', token: null, apiResponse: null }
      return
    }

    if (permStatus.receive !== 'granted') {
      console.log('Push: requesting permissions…')
      const reqResult = await PushNotifications.requestPermissions()
      console.log('Push: permission request result:', reqResult.receive)
      if (reqResult.receive !== 'granted') {
        console.warn('Push: permissions not granted after request')
        _pushRegistrationState = { status: 'permission-not-granted', error: 'Permission not granted: ' + reqResult.receive, token: null, apiResponse: null }
        return
      }
    }

    console.log('Push: permissions OK, setting up listeners…')

    // Remove any existing listeners to avoid duplicates
    await PushNotifications.removeAllListeners()

    // Listen for successful registration (receives FCM token)
    PushNotifications.addListener('registration', async (token) => {
      console.log('Push: ✅ FCM registration succeeded, token:', token.value?.substring(0, 30) + '…')
      _pushRegistrationState.token = token.value?.substring(0, 20) + '…'

      // Save token to backend
      try {
        const idToken = sessionStorage.getItem('id_token')
        if (!idToken) {
          const msg = 'no id_token in sessionStorage, cannot save device token'
          console.error('Push:', msg)
          _pushRegistrationState = { ..._pushRegistrationState, status: 'error', error: msg }
          return
        }

        const platform = window.Capacitor?.getPlatform?.() || 'unknown'
        const payload = {
          DEVICE_TOKEN: token.value,
          COMMUNITY_ID: communityId,
          UNIT_NBR: unitNbr,
          EMAIL: email,
          PLATFORM: platform
        }
        console.log('Push: sending token to backend…', { COMMUNITY_ID: communityId, UNIT_NBR: unitNbr, PLATFORM: platform })

        const resp = await fetch(`${API_BASE}/device-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`
          },
          body: JSON.stringify(payload)
        })

        let respBody = ''
        try { respBody = await resp.text() } catch (_) {}
        console.log('Push: backend response status=' + resp.status + ', body=' + respBody)

        if (resp.ok) {
          console.log('Push: ✅ device token saved to backend successfully')
          _pushRegistrationState = { ..._pushRegistrationState, status: 'registered', apiResponse: resp.status }
          try { sessionStorage.setItem('push_token', token.value) } catch (e) { }
        } else {
          const msg = 'backend returned ' + resp.status + ': ' + respBody
          console.error('Push: ❌ failed to save device token:', msg)
          _pushRegistrationState = { ..._pushRegistrationState, status: 'api-error', error: msg, apiResponse: resp.status }
        }
      } catch (err) {
        console.error('Push: ❌ error saving device token:', err)
        _pushRegistrationState = { ..._pushRegistrationState, status: 'error', error: err.message }
      }
    })

    // Listen for registration errors
    PushNotifications.addListener('registrationError', (err) => {
      console.error('Push: ❌ FCM registration failed:', JSON.stringify(err))
      _pushRegistrationState = { ..._pushRegistrationState, status: 'fcm-error', error: 'FCM registration failed: ' + (err?.error || JSON.stringify(err)) }
    })

    // Listen for push notifications received while app is in foreground
    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      console.log('Push: notification received in foreground:', notification)
      addNotification({ title: notification.title, body: notification.body, data: notification.data })
      showInAppNotification(notification)
    })

    // Listen for push notification action (user tapped on notification)
    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      console.log('Push: notification action performed:', action)
      const n = action?.notification
      const notifData = { title: n?.title, body: n?.body, data: n?.data || {} }
      if (n?.title) {
        addNotification({ title: n.title, body: n.body, data: n.data || {} })
      }
      // Fire tap event with notification data so panel opens and can navigate
      _fireNotificationTap(notifData)
    })

    // Create a notification channel (required on Android 8+)
    if (detectedPlatform === 'android') {
      try {
        await PushNotifications.createChannel({
          id: 'smart-comm-default',
          name: 'SmartComm Notifications',
          description: 'General notifications from SmartComm',
          importance: 5,
          visibility: 1,
          sound: 'default',
          vibration: true,
        })
        console.log('Push: notification channel created')
      } catch (chErr) {
        console.warn('Push: channel creation error (non-critical):', chErr)
      }
    }

    // Register with FCM
    console.log('Push: calling PushNotifications.register()…')
    _pushRegistrationState.status = 'registering-fcm'
    await PushNotifications.register()
    console.log('Push: register() resolved — waiting for FCM token callback…')
  } catch (err) {
    console.error('Push: ❌ setup failed:', err?.message || err, err?.stack)
    _pushRegistrationState = { status: 'setup-error', error: err?.message || String(err), token: null, apiResponse: null }
    throw err // re-throw so callers' .catch() can log it
  }
}

/**
 * Unregister push notifications (e.g., on sign-out).
 * Removes the device token from the backend.
 */
export async function unregisterPushNotifications() {
  try {
    const pushToken = sessionStorage.getItem('push_token')
    const communityId = sessionStorage.getItem('community_id')
    const idToken = sessionStorage.getItem('id_token')

    if (pushToken && communityId && idToken) {
      // Attempt to get unit number from session
      // (ManagementPortal stores it, but it might not be available)
      const unitNbr = sessionStorage.getItem('unit_nbr') || ''

      await fetch(`${API_BASE}/device-token`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({
          DEVICE_TOKEN: pushToken,
          COMMUNITY_ID: communityId,
          UNIT_NBR: unitNbr
        })
      })
      console.log('Push: device token removed from backend')
    }

    const { PushNotifications } = await import('@capacitor/push-notifications')
    await PushNotifications.removeAllListeners()
    console.log('Push: listeners removed')
  } catch (err) {
    console.warn('Push: unregister error (non-critical):', err)
  }
}

/**
 * Show an in-app notification when a push arrives while the app is in the foreground.
 */
function showInAppNotification(notification) {
  const { title, body } = notification

  // Create a floating toast element
  const toast = document.createElement('div')
  toast.style.cssText = `
    position: fixed; top: 60px; left: 16px; right: 16px; z-index: 99999;
    background: #1e40af; color: white; padding: 14px 18px; border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.3); font-family: -apple-system, system-ui, sans-serif;
    animation: slideDown 0.3s ease-out; cursor: pointer;
  `
  const titleEl = document.createElement('div')
  titleEl.style.cssText = 'font-weight: 600; font-size: 14px; margin-bottom: 2px;'
  titleEl.textContent = title || 'Notification'
  const bodyEl = document.createElement('div')
  bodyEl.style.cssText = 'font-size: 13px; opacity: 0.9;'
  bodyEl.textContent = body || ''
  toast.appendChild(titleEl)
  toast.appendChild(bodyEl)

  // Add slide-down animation
  const style = document.createElement('style')
  style.textContent = `
    @keyframes slideDown { from { transform: translateY(-100%); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
    @keyframes slideUp { from { transform: translateY(0); opacity: 1; } to { transform: translateY(-100%); opacity: 0; } }
  `
  document.head.appendChild(style)
  document.body.appendChild(toast)

  // Dismiss on tap
  toast.addEventListener('click', () => {
    toast.style.animation = 'slideUp 0.3s ease-in forwards'
    setTimeout(() => toast.remove(), 300)
  })

  // Auto-dismiss after 4 seconds
  setTimeout(() => {
    if (toast.parentNode) {
      toast.style.animation = 'slideUp 0.3s ease-in forwards'
      setTimeout(() => toast.remove(), 300)
    }
  }, 4000)
}
